import React, { useCallback, useContext, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { HELP_DESK_VIEWS, HELP_DESK_VIEW_VALUES } from '@ubeya/shared/constants/helpDesk';
import { FlexMiddle, LinkTab } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { useHelpDeskAnalytics } from '../../helpDeskAnalytics';

const TabsRow = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 8px 16px;
`;

const TabItem = styled(LinkTab)`
  padding: 4px 16px;
  ${FlexMiddle};
  gap: 8px;
  margin: 0;
  min-width: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  line-height: 22px;
  font-weight: 400;
`;

const TabCount = styled.span`
  color: ${({ theme }) => theme.colors.notification};
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
`;

const HELP_DESK_TAB_COUNT_KEYS = {
  [HELP_DESK_VIEW_VALUES.ASSIGNED_TO_ME]: 'inbox',
  [HELP_DESK_VIEW_VALUES.MENTIONS]: 'mentions',
  [HELP_DESK_VIEW_VALUES.ALL]: 'all'
};

const HelpDeskTabs = ({ value, onChange, tabCounts = {} }) => {
  const { t } = useContext(ConfigContext);
  const { trackHelpDeskEvent } = useHelpDeskAnalytics();
  const lastTabCountsRef = useRef({});
  const hasTabCounts = Object.keys(tabCounts).length > 0;
  const displayedTabCounts = hasTabCounts ? tabCounts : lastTabCountsRef.current;

  useEffect(() => {
    if (hasTabCounts) {
      lastTabCountsRef.current = tabCounts;
    }
  }, [hasTabCounts, tabCounts]);

  const handleTabChange = useCallback(
    (tabValue, labelKey) => {
      if (tabValue === value) return;

      const prevTab = HELP_DESK_VIEWS.find(({ value: viewValue }) => viewValue === value);

      trackHelpDeskEvent({
        eventName: 'Help Desk - Admin Switched Tab',
        context: 'Help Desk tab switched',
        eventProperties: {
          tabName: t(labelKey),
          prevTabName: prevTab ? t(prevTab.labelKey) : undefined
        }
      });

      onChange(tabValue);
    },
    [onChange, t, trackHelpDeskEvent, value]
  );

  return (
    <TabsRow>
      {HELP_DESK_VIEWS.map(({ value: tabValue, labelKey }) => {
        const countKey = HELP_DESK_TAB_COUNT_KEYS[tabValue];
        const count = displayedTabCounts?.[countKey] ?? 0;

        return (
          <TabItem key={tabValue} isActive={value === tabValue} onClick={() => handleTabChange(tabValue, labelKey)}>
            {t(labelKey)}
            {count > 0 && <TabCount>{count}</TabCount>}
          </TabItem>
        );
      })}
    </TabsRow>
  );
};

export default HelpDeskTabs;
